import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useRoute } from 'wouter';
import { Button } from '~/modules/Elements/AKUI/Button';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';
import { useBackground } from '~/modules/Elements/BackgroundContext';
import { useEventEffect } from '~/modules/GameEvents/hooks';
import Chat from '~/modules/Multiplayer/components/Chat';
import { useIsHost, useIsInRoom, useMyPlayerId, useReconnecting } from '~/modules/Multiplayer/hooks/useIsHost';
import { useRoom } from '~/modules/Multiplayer/hooks/useRoom';
import MultiplayerManager from '~/modules/Multiplayer/MultiplayerManager';
import RoomStateStore, { countdownStarted, kicked, roomError } from '~/modules/Multiplayer/RoomState';
import { playerColor } from '~/modules/Multiplayer/playerColors';
import { twx } from '~/utils/twx';

const PLAYER_COLORS = [
  'bg-pink-500/30',
  'bg-blue-500/30',
  'bg-green-500/30',
  'bg-yellow-500/30',
  'bg-purple-500/30',
  'bg-cyan-500/30',
  'bg-orange-500/30',
  'bg-red-500/30',
  'bg-indigo-500/30',
  'bg-teal-500/30',
  'bg-lime-500/30',
  'bg-fuchsia-500/30',
  'bg-amber-500/30',
  'bg-emerald-500/30',
  'bg-rose-500/30',
  'bg-violet-500/30',
];

function useMicrophonePermission() {
  const [micStatus, setMicStatus] = useState<'pending' | 'granted' | 'denied' | 'error'>('pending');

  useEffect(() => {
    let cancelled = false;

    const checkMic = () => {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          stream.getTracks().forEach((t) => t.stop());
          if (!cancelled) setMicStatus('granted');
        })
        .catch((err) => {
          if (!cancelled) setMicStatus(err.name === 'NotAllowedError' ? 'denied' : 'error');
        });
    };

    checkMic();

    // Listen for permission changes (e.g. user grants access after initial denial)
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then((result) => {
          result.addEventListener('change', () => {
            if (!cancelled) checkMic();
          });
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return micStatus;
}

function RoomLobby() {
  const [, setLocation] = useLocation();
  const [, params] = useRoute('room/:code');
  useBackground(true);
  const micStatus = useMicrophonePermission();

  const room = useRoom();
  const isHost = useIsHost();
  const isInRoom = useIsInRoom();
  const myId = useMyPlayerId();
  const isReconnecting = useReconnecting();
  const [notification, setNotification] = useState<{ message: string; type: 'error' | 'info' } | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [reconnectTimeout, setReconnectTimeout] = useState(false);

  useEffect(() => {
    if (!isReconnecting) {
      setReconnectTimeout(false);
      return;
    }
    const id = setTimeout(() => setReconnectTimeout(true), 20000);
    return () => clearTimeout(id);
  }, [isReconnecting]);

  const roomCode = params?.code || room?.code || '';
  const joinUrl = `${window.location.origin}/?join=${roomCode}`;

  // Redirect if not in room
  useEffect(() => {
    if (!isInRoom && roomCode) {
      const timeout = setTimeout(() => {
        if (!RoomStateStore.isInRoom) {
          setLocation('');
        }
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [isInRoom]);

  useEventEffect(kicked, () => {
    setNotification({ message: 'Zostałeś wyrzucony z pokoju', type: 'error' });
    setTimeout(() => setLocation(''), 1500);
  });

  useEventEffect(roomError, (message?: string) => {
    setNotification({ message: message || 'Błąd połączenia', type: 'error' });
    setTimeout(() => setLocation(''), 2000);
  });

  const copyRoomCode = () => {
    const doCopy = navigator.clipboard?.writeText
      ? navigator.clipboard.writeText(joinUrl)
      : new Promise<void>((resolve) => {
          const ta = document.createElement('textarea');
          ta.value = joinUrl;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          resolve();
        });
    doCopy.then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  useEventEffect(countdownStarted, () => {
    setLocation(`room/${roomCode}/game`);
  });

  const handleStartGame = () => {
    setLocation(`room/${roomCode}/game`);
  };

  const handleLeave = () => {
    MultiplayerManager.leaveRoom();
    setLocation('');
  };

  if (!room && !notification) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Typography className="animate-pulse text-xl">Ładowanie...</Typography>
      </div>
    );
  }

  if (notification) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Box className={`gap-2 rounded-xl p-8 ${notification.type === 'error' ? 'bg-red-900/60' : 'bg-black/75'}`}>
          <Typography className="text-xl">{notification.message}</Typography>
          <Typography className="text-sm text-white/50">Przekierowywanie...</Typography>
        </Box>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Pokój {roomCode} - Karaoke Party</title>
      </Helmet>
      {isReconnecting && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-black/90 px-8 py-6 ring-1 ring-white/20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            <span className="text-lg text-white">Ponowne łączenie...</span>
            <span className="text-sm text-white/50">Nie zamykaj strony</span>
            {reconnectTimeout && (
              <button
                onClick={handleLeave}
                className="mt-2 rounded-lg bg-red-500/30 px-4 py-2 text-sm text-red-200 transition-colors hover:bg-red-500/50">
                Opuść pokój
              </button>
            )}
          </div>
        </div>
      )}
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col items-center justify-center gap-5 p-4">
        {/* Header */}
        <Typography className="bg-gradient-to-r from-yellow-300 via-pink-400 to-purple-400 bg-clip-text text-2xl font-extrabold tracking-widest text-transparent uppercase">
          Karaoke Party
        </Typography>

        {/* Room Code Section */}
        <Box className="w-full gap-4 bg-black/70 p-6 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <Typography className="text-xs font-semibold tracking-wider text-white/50 uppercase">Kod pokoju</Typography>
            <CopyButton onClick={copyRoomCode}>{codeCopied ? 'Skopiowano!' : 'Kopiuj link zaproszenia'}</CopyButton>
          </div>
          <div className="flex items-center gap-6">
            <RoomCodeDisplay>{roomCode}</RoomCodeDisplay>
            <div className="ml-auto rounded-lg bg-white p-2">
              <QRCodeSVG value={joinUrl} size={100} className="h-auto w-16 sm:w-24 md:w-[100px]" />
            </div>
          </div>
        </Box>

        {/* Players List */}
        <Box className="w-full gap-3 bg-black/70 p-6 backdrop-blur-sm">
          <div className="flex w-full items-center justify-between">
            <Typography className="text-sm font-semibold tracking-wider text-white/50 uppercase">Gracze</Typography>
            <Typography className="rounded-full bg-white/10 px-3 py-0.5 text-sm">{room!.players.length}/16</Typography>
          </div>
          <div className="mt-1 grid w-full grid-cols-2 gap-2 sm:grid-cols-3">
            {room!.players.map((player, i) => {
              const isMe = player.id === myId;
              return (
                <div
                  key={player.id}
                  className={`flex items-center justify-between rounded-lg ${PLAYER_COLORS[i % PLAYER_COLORS.length]} px-3 py-2.5 transition-all ${
                    isMe ? 'ring-1 ring-white/30' : ''
                  } ${!player.isConnected ? 'opacity-40' : ''}`}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    <PlayerAvatar color={player.color ?? i}>{player.name[0]?.toUpperCase()}</PlayerAvatar>
                    <div className="flex flex-col overflow-hidden">
                      <span className={`truncate text-sm ${isMe ? 'font-bold' : ''}`}>
                        {player.name}
                        {isMe && ' (Ty)'}
                      </span>
                      {player.isHost && (
                        <span className="text-[10px] font-semibold text-yellow-300 uppercase">Host</span>
                      )}
                    </div>
                  </div>
                  {isHost && !player.isHost && (
                    <KickButton onClick={() => MultiplayerManager.kickPlayer(player.id)} title="Wyrzuć gracza">
                      ✕
                    </KickButton>
                  )}
                </div>
              );
            })}
          </div>
        </Box>

        {/* Chat */}
        <Chat />

        {/* Microphone Status */}
        {micStatus !== 'granted' && (
          <Box className="w-full gap-2 bg-black/70 p-4 backdrop-blur-sm">
            {micStatus === 'pending' && (
              <Typography className="animate-pulse text-center text-sm text-yellow-300">
                Zezwól na dostęp do mikrofonu...
              </Typography>
            )}
            {micStatus === 'denied' && (
              <Typography className="text-center text-sm text-red-400">
                Mikrofon zablokowany! Odblokuj w ustawieniach przeglądarki aby zdobywać punkty.
              </Typography>
            )}
            {micStatus === 'error' && (
              <Typography className="text-center text-sm text-red-400">
                Nie wykryto mikrofonu. Podłącz mikrofon lub użyj urządzenia z mikrofonem.
              </Typography>
            )}
          </Box>
        )}

        {/* Actions */}
        <Box className="w-full gap-3 bg-black/70 p-6 backdrop-blur-sm">
          {isHost ? (
            <>
              <ActionButton onClick={handleStartGame} disabled={room!.players.length < 1}>
                Wybierz piosenkę i graj!
              </ActionButton>
              <Typography className="text-center text-xs text-white/40">
                Jako host kontrolujesz wybór piosenek i start gry
              </Typography>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              <Typography className="animate-pulse text-lg text-white/70">Czekam na hosta...</Typography>
              <Typography className="text-xs text-white/40">Host wybierze piosenkę i rozpocznie grę</Typography>
            </div>
          )}
          <LeaveButton onClick={handleLeave}>Opuść pokój</LeaveButton>
        </Box>
      </div>
    </>
  );
}

function PlayerAvatar({ children, color }: { children: React.ReactNode; color: number }) {
  return (
    <div
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${playerColor(color).bg} text-xs font-bold text-white`}>
      {children}
    </div>
  );
}

const RoomCodeDisplay = twx.div`font-mono text-4xl font-bold tracking-[0.4em] text-white select-all sm:text-5xl`;

const CopyButton = twx.button`cursor-pointer rounded-full bg-white/10 px-4 py-1 text-xs text-white/70 transition-all hover:bg-white/20 hover:text-white`;

const KickButton = twx.button`cursor-pointer rounded-full bg-red-500/20 px-2 py-0.5 text-xs text-red-300 transition-colors hover:bg-red-500/40`;

const ActionButton = twx(
  Button,
)`h-16 w-full bg-gradient-to-r from-green-700/70 to-green-600/70 text-xl transition-all hover:from-green-600/80 hover:to-green-500/80`;

const LeaveButton = twx(
  Button,
)`h-10 w-full bg-white/5 text-sm text-white/50 transition-colors hover:bg-red-900/30 hover:text-red-300`;

export default RoomLobby;
